import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Factory, Users, Zap, Cloud, Cpu } from 'lucide-react';
import { renewableServices as mockServices } from '../data/mock';
import { contentAPI } from '../services/api';

const RenewableConsulting = () => {
  const [renewableServices, setRenewableServices] = useState(mockServices);

  useEffect(() => {
    window.scrollTo(0, 0);
    contentAPI.get('renewable_services').then(res => {
      if (Array.isArray(res.data) && res.data.length) setRenewableServices(res.data);
    }).catch(() => {});
  }, []);

  const iconMap = {
    Cpu: Cpu,
    Building2: Building2,
    Factory: Factory,
    Users: Users,
    Zap: Zap,
    Cloud: Cloud
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-96 flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Renewable Energy Consulting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 to-gray-900/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Renewable Energy Consulting</h1>
          <p className="text-xl text-gray-200 max-w-2xl">
            End-to-end consulting services for solar, wind, and hybrid renewable energy projects
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Comprehensive Renewable Solutions</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              From feasibility studies to commissioning support, we provide complete consulting services for renewable energy projects. Our expertise spans rooftop solar, ground-mounted installations, captive power plants, group captive models, DISCOM PPAs, and virtual PPAs.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {renewableServices.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Link
                  key={service.id}
                  to={`/renewable/${service.slug}`}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-emerald-600 transition-colors duration-200">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  
                  <div className="flex items-center text-emerald-600 font-semibold group-hover:gap-2 transition-all duration-200">
                    Learn More
                    <ArrowRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Consulting Process</h2>
            <p className="text-lg text-gray-600">Structured approach to deliver successful projects</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Feasibility Study', desc: 'Site assessment and viability analysis' },
              { step: '02', title: 'Techno-Commercial', desc: 'Technical design and financial modeling' },
              { step: '03', title: 'Regulatory Approvals', desc: 'All statutory clearances and licenses' },
              { step: '04', title: 'Implementation Support', desc: 'Commissioning and handover assistance' }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="inline-flex w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full items-center justify-center mb-4 text-white font-bold text-xl">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Renewable Project?
          </h2>
          <p className="text-xl text-emerald-50 mb-8">
            Get expert guidance from feasibility to commissioning
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-emerald-600 font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            Request Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RenewableConsulting;
